import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import SectionTitle from "./SectionTitle";
import { TESTIMONIALS_API } from "../../data/site";

const getFirstString = (item, keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const getSortTimeFromItem = (item) => {
  const dateKeys = [
    "createdAt",
    "created_at",
    "publishedAt",
    "published_at",
    "date",
    "timestamp",
  ];

  for (const key of dateKeys) {
    const value = item?.[key];
    if (!value) continue;

    const parsedTime = new Date(value).getTime();
    if (!Number.isNaN(parsedTime)) {
      return parsedTime;
    }
  }

  return Number.POSITIVE_INFINITY;
};

const normalizeVideoTestimonial = (item, index) => {
  const location = getFirstString(item, ["location", "city", "subtitle", "category"]);

  return {
    id: item?._id || item?.id || `video-testimonial-${index}`,
    name: getFirstString(item, ["patientName", "name", "label", "title"]) || `Video ${index + 1}`,
    location: location ? location.replace(/[-_]/g, " ") : "Patient testimonial",
    description: getFirstString(item, ["description", "text", "quote"]),
    videoUrl: getFirstString(item, ["video", "videoUrl", "video_url", "url", "mediaUrl"]),
  };
};

const preparePreviewFrame = (videoElement) => {
  if (!videoElement || videoElement.dataset.previewReady === "true") {
    return;
  }

  const seekToPreviewFrame = () => {
    videoElement.dataset.previewReady = "true";

    try {
      videoElement.currentTime = 0.1;
    } catch {
      videoElement.dataset.previewReady = "failed";
    }
  };

  if (videoElement.readyState >= 2) {
    seekToPreviewFrame();
    return;
  }

  videoElement.addEventListener("loadeddata", seekToPreviewFrame, { once: true });
};

const VideoTestimonialsFilmRoll = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeVideo, setActiveVideo] = useState(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
    dragFree: true,
  });

  useEffect(() => {
    const controller = new AbortController();

    const loadVideoTestimonials = async () => {
      setLoading(true);
      setError("");

      try {
        if (!TESTIMONIALS_API?.url) {
          throw new Error("Testimonials API URL is missing.");
        }

        const params = new URLSearchParams({
          limit: "12",
          category: "testimonial",
          sortBy: "createdAt",
          sortOrder: "asc",
        });
        const headers = TESTIMONIALS_API?.token
          ? { Authorization: `Bearer ${TESTIMONIALS_API.token}` }
          : {};

        const response = await fetch(`${TESTIMONIALS_API.url}?${params.toString()}`, {
          signal: controller.signal,
          headers,
        });

        const payload = await response.json();

        if (!response.ok || !payload?.success) {
          throw new Error(payload?.message || "Unable to load testimonials.");
        }

        const liveVideos = Array.isArray(payload?.data)
          ? payload.data
              .map((item, index) => ({
                testimonial: normalizeVideoTestimonial(item, index),
                sortTime: getSortTimeFromItem(item),
                sourceIndex: index,
              }))
              .filter(({ testimonial }) => testimonial.videoUrl)
              .sort((left, right) => {
                if (left.sortTime === right.sortTime) {
                  return left.sourceIndex - right.sourceIndex;
                }
                return left.sortTime - right.sortTime;
              })
              .map(({ testimonial }) => testimonial)
          : [];

        if (!liveVideos.length) {
          throw new Error("No published video testimonials were returned.");
        }

        setVideos(liveVideos);
      } catch (err) {
        if (err.name === "AbortError") return;
        setVideos([]);
        setError(err.message || "Unable to load video testimonials.");
      } finally {
        setLoading(false);
      }
    };

    loadVideoTestimonials();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!emblaApi || !videos.length) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();

    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, videos.length]);

  useEffect(() => {
    if (!emblaApi || videos.length < 2) return;

    const intervalId = setInterval(() => {
      emblaApi.scrollNext();
    }, 10000);

    return () => clearInterval(intervalId);
  }, [emblaApi, videos.length]);

  const activeVideoIndex = activeVideo
    ? videos.findIndex((v) => v.id === activeVideo.id)
    : -1;

  const showPreviousVideo = useCallback(() => {
    setActiveVideo((current) => {
      if (!current || videos.length < 2) return current;
      const currentIndex = videos.findIndex((v) => v.id === current.id);
      if (currentIndex === -1) return current;
      return videos[(currentIndex - 1 + videos.length) % videos.length];
    });
  }, [videos]);

  const showNextVideo = useCallback(() => {
    setActiveVideo((current) => {
      if (!current || videos.length < 2) return current;
      const currentIndex = videos.findIndex((v) => v.id === current.id);
      if (currentIndex === -1) return current;
      return videos[(currentIndex + 1) % videos.length];
    });
  }, [videos]);

  useEffect(() => {
    if (!activeVideo) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveVideo(null);
      } else if (event.key === "ArrowLeft") {
        showPreviousVideo();
      } else if (event.key === "ArrowRight") {
        showNextVideo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeVideo, showPreviousVideo, showNextVideo]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  if (error || (!loading && !videos.length)) {
    return null;
  }

  return (
    <section className="bg-[#F4ECE0] py-20 border-t border-[#B79A68]/20">
      <div className="max-w-7xl mx-auto px-6">
        <SectionTitle script="" title="Video Testimonials" subtitle="Real Patient Stories" />

        {!videos.length && loading ? (
          <p className="mt-12 text-center text-sm tracking-[0.2em] uppercase text-[#7D6A42]/80">
            Loading video testimonials...
          </p>
        ) : (
          <>
            <div className="mt-12 bg-[#1A1A1A] rounded-sm overflow-hidden shadow-2xl border border-white/5">
              <div className="flex items-center bg-[#111] h-8 overflow-hidden">
                {[...Array(40)].map((_, i) => (
                  <div key={`top-hole-${i}`} className="flex-shrink-0 w-8 flex justify-center">
                    <div className="w-5 h-4 bg-[#1A1A1A] rounded-sm border border-white/10" />
                  </div>
                ))}
              </div>

              <div className="flex items-stretch">
                <div className="hidden md:flex flex-shrink-0 w-8 bg-[#111] flex-col justify-around py-3 gap-2 min-h-full">
                  {[...Array(6)].map((_, i) => (
                    <div key={`left-hole-${i}`} className="mx-auto w-4 h-3 bg-[#1A1A1A] rounded-sm border border-white/10" />
                  ))}
                </div>

                <div className="flex-1 overflow-hidden py-4 px-2" ref={emblaRef}>
                  <div className="flex gap-3">
                    {videos.map((v, i) => (
                      <div
                        key={v.id}
                        className={`flex-none w-[260px] md:w-[300px] lg:w-[340px] group ${v.videoUrl ? "cursor-pointer" : "cursor-default"}`}
                        onClick={() => v.videoUrl && setActiveVideo(v)}
                        role={v.videoUrl ? "button" : undefined}
                        tabIndex={v.videoUrl ? 0 : -1}
                        onKeyDown={(event) => {
                          if (!v.videoUrl) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setActiveVideo(v);
                          }
                        }}
                      >
                        <div className="bg-[#111] px-3 py-1 flex items-center justify-between">
                          <span className="text-[9px] tracking-[0.3em] uppercase text-white/30 font-mono">
                            VIDEO {String(i + 1).padStart(2, "0")}
                          </span>
                        </div>

                        <div className="relative aspect-[4/3] overflow-hidden border-x border-white/5 bg-black">
                          <video
                            ref={preparePreviewFrame}
                            src={v.videoUrl}
                            muted
                            playsInline
                            preload="metadata"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                          />

                          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/75" />
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/5" />

                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-14 h-14 border border-[#B79A68]/70 group-hover:bg-[#B79A68] group-hover:border-[#B79A68] bg-[#20322E]/30 flex items-center justify-center transition-all duration-300">
                              <Play size={18} strokeWidth={1.2} className="text-[#E9D7B1] group-hover:text-[#20322E] ml-0.5" fill="currentColor" />
                            </div>
                          </div>

                          <div className="absolute inset-x-0 top-6 text-center px-6">
                            <div className="text-[10px] tracking-[0.35em] uppercase text-white/35 font-mono">
                              Patient Story
                            </div>
                          </div>

                          <div className="absolute bottom-0 left-0 right-0 p-4">
                            <div className="text-xs tracking-[0.25em] uppercase text-white font-medium">{v.name}</div>
                            <div className="text-[10px] tracking-widest uppercase text-white/55 mt-0.5">{v.location}</div>
                          </div>
                        </div>

                        <div className="bg-[#111] px-3 py-1">
                          <div className="w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                          {v.description && (
                            <div className="pt-2 text-[10px] tracking-[0.16em] uppercase text-white/35 truncate">
                              {v.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="hidden md:flex flex-shrink-0 w-8 bg-[#111] flex-col justify-around py-3 gap-2 min-h-full">
                  {[...Array(6)].map((_, i) => (
                    <div key={`right-hole-${i}`} className="mx-auto w-4 h-3 bg-[#1A1A1A] rounded-sm border border-white/10" />
                  ))}
                </div>
              </div>

              <div className="flex items-center bg-[#111] h-8 overflow-hidden">
                {[...Array(40)].map((_, i) => (
                  <div key={`bottom-hole-${i}`} className="flex-shrink-0 w-8 flex justify-center">
                    <div className="w-5 h-4 bg-[#1A1A1A] rounded-sm border border-white/10" />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 mt-8 text-[#5F4F30]">
              <button
                onClick={scrollPrev}
                className="w-11 h-11 border border-[#B79A68]/40 flex items-center justify-center hover:border-[#B79A68] hover:text-[#B79A68] transition-colors bg-[#ffffff]"
                aria-label="Previous"
              >
                <ChevronLeft size={18} strokeWidth={1.5} />
              </button>
              <span className="text-[10px] tracking-[0.35em] uppercase text-[#7D6A42]/70 font-mono">drag or click</span>
              <button
                onClick={scrollNext}
                className="w-11 h-11 border border-[#B79A68]/40 flex items-center justify-center hover:border-[#B79A68] hover:text-[#B79A68] transition-colors bg-[#ffffff]"
                aria-label="Next"
              >
                <ChevronRight size={18} strokeWidth={1.5} />
              </button>
            </div>

            {videos.length > 0 && (
              <div className="flex items-center justify-center gap-3 mt-5">
                {videos.map((_, i) => (
                  <button
                    key={`indicator-${i}`}
                    onClick={() => emblaApi?.scrollTo(i)}
                    className={`transition-all duration-300 rounded-full ${
                      i === selectedIndex % videos.length
                        ? "w-6 h-1.5 bg-[#B79A68]"
                        : "w-1.5 h-1.5 bg-[#B79A68]/35 hover:bg-[#B79A68]/60"
                    }`}
                    aria-label={`Go to video ${i + 1}`}
                  />
                ))}
                <span className="ml-2 text-[10px] tracking-[0.3em] uppercase text-[#7D6A42]/80 font-mono">
                  {(selectedIndex % videos.length) + 1} / {videos.length}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {activeVideo && (
        <div
          className="fixed inset-0 z-50 bg-black/85 px-4 py-6 md:px-8 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={activeVideo.name}
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="w-full max-w-5xl border border-white/10 bg-[#111] shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-white/10 text-white">
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-[#B79A68]">Now Playing</div>
                <div className="mt-1 text-lg md:text-xl">{activeVideo.name}</div>
                <div className="text-xs tracking-[0.2em] uppercase text-white/45 mt-1">{activeVideo.location}</div>
              </div>
              <button
                type="button"
                className="border border-white/15 px-4 py-2 text-xs tracking-[0.2em] uppercase text-white/70 hover:border-[#B79A68] hover:text-[#B79A68] transition-colors"
                onClick={() => setActiveVideo(null)}
              >
                Close
              </button>
            </div>

            <div className="relative bg-black">
              <video
                key={activeVideo.id}
                src={activeVideo.videoUrl}
                controls
                autoPlay
                playsInline
                className="w-full max-h-[75vh] bg-black"
              >
                Your browser does not support embedded video playback.
              </video>

              {videos.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={showPreviousVideo}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 md:w-11 md:h-11 border border-white/20 flex items-center justify-center text-white hover:border-[#B79A68] hover:text-[#B79A68] transition-colors bg-black/50 backdrop-blur-sm"
                    aria-label="Previous video"
                  >
                    <ChevronLeft size={20} strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={showNextVideo}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 md:w-11 md:h-11 border border-white/20 flex items-center justify-center text-white hover:border-[#B79A68] hover:text-[#B79A68] transition-colors bg-black/50 backdrop-blur-sm"
                    aria-label="Next video"
                  >
                    <ChevronRight size={20} strokeWidth={1.5} />
                  </button>
                </>
              )}
            </div>

            {activeVideoIndex !== -1 && videos.length > 1 && (
              <div className="px-4 pt-3 text-center text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono">
                {activeVideoIndex + 1} / {videos.length}
              </div>
            )}

            {activeVideo.description && (
              <div className="px-4 py-3 text-sm text-white/70 border-t border-white/10">
                {activeVideo.description}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default VideoTestimonialsFilmRoll;
